import { agentKeys } from "@/features/agents/hooks/use-agents";
import { clientKeys } from "@/features/clients/hooks/use-clients";
import { connectorKeys } from "@/features/connectors/hooks/use-connectors";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OnboardingAPI, type SaveDraftPayload } from "../api/onboarding";
import type {
  CallbackStatus,
  OnboardingDraft,
  OnboardingState,
} from "../types";

export const onboardingKeys = {
  all: ["onboarding"] as const,
  state: (clientId: string) => [...onboardingKeys.all, clientId] as const,
};

/** The client id the wizard runs for. It always lives under `/:company`. */
export function useOnboardingClientId() {
  const { company } = useParams<{ company: string }>();
  return company || "";
}

/**
 * The wizard's server state.
 *
 * Polls only while the analysis is in flight — the rest of the time the wizard
 * owns this data locally and a refetch would fight the form. Every write below
 * pushes its response straight into the cache, so nothing else needs polling.
 */
export function useOnboarding(options?: { enabled?: boolean }) {
  const clientId = useOnboardingClientId();

  return useQuery<OnboardingState>({
    queryKey: onboardingKeys.state(clientId),
    queryFn: () => OnboardingAPI.getState(clientId),
    enabled: !!clientId && options?.enabled !== false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      query.state.data?.onboarding.analysis_status === "running"
        ? 3_000
        : false,
  });
}

/**
 * Send owners into the wizard when it is waiting for them.
 *
 * `pending` means nobody has started it — a brand-new tenant, or one support
 * has reset. That is the only state worth interrupting for: `in_progress`
 * belongs to someone who walked away mid-way and would resent being dragged
 * back every time they open the app, and `skipped` is an answer.
 *
 * Owners only, because the wizard rewrites the whole tenant — and it is
 * mounted where the back-office is, so redirecting out of it can't loop.
 */
export function useLaunchOnboardingForOwners() {
  const clientId = useOnboardingClientId();
  const navigate = useNavigate();
  const isOwner = useHasRole("OWNER");
  // Nobody else can act on the answer, so nobody else pays for the request.
  const { data } = useOnboarding({ enabled: isOwner });

  const status = data?.onboarding.status;

  useEffect(() => {
    if (!clientId || !isOwner || status !== "pending") return;
    navigate(`/${clientId}/onboarding`, { replace: true });
  }, [clientId, isOwner, status, navigate]);
}

export function useStartAnalysis() {
  const clientId = useOnboardingClientId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { url?: string; description?: string }) =>
      OnboardingAPI.startAnalysis(clientId, input),
    onSuccess: (onboarding) => {
      queryClient.setQueryData<OnboardingState>(
        onboardingKeys.state(clientId),
        (previous) =>
          previous
            ? { ...previous, onboarding }
            : { onboarding, demo_number: null },
      );
    },
  });
}

export function useSaveOnboardingDraft() {
  const clientId = useOnboardingClientId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveDraftPayload) =>
      OnboardingAPI.saveDraft(clientId, payload),
    onSuccess: (onboarding) => {
      queryClient.setQueryData<OnboardingState>(
        onboardingKeys.state(clientId),
        (previous) =>
          previous
            ? { ...previous, onboarding }
            : { onboarding, demo_number: null },
      );
    },
  });
}

export function useCompleteOnboarding() {
  const clientId = useOnboardingClientId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (draft?: OnboardingDraft) =>
      OnboardingAPI.complete(clientId, draft),
    onSuccess: () => {
      // `complete` rewrites the client, the agent and the connectors in one
      // go — drop the caches that now describe the old setup.
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
      queryClient.invalidateQueries({ queryKey: agentKeys.list(clientId) });
      queryClient.invalidateQueries({ queryKey: connectorKeys.all });
    },
  });
}

/** "Appelez-moi". */
export function useRequestCallback() {
  const clientId = useOnboardingClientId();
  return useMutation({
    mutationFn: () => OnboardingAPI.requestCallback(clientId),
  });
}

/**
 * The call's status, polled while it is on. Stops on its own once the call is
 * over: nothing about it changes after that.
 */
export function useCallbackStatus(sessionId: string | null) {
  const clientId = useOnboardingClientId();
  return useQuery<CallbackStatus>({
    queryKey: [...onboardingKeys.all, clientId, "callback", sessionId],
    queryFn: () => OnboardingAPI.getCallbackStatus(clientId, sessionId || ""),
    enabled: !!clientId && !!sessionId,
    refetchInterval: (query) =>
      query.state.data && query.state.data !== "calling" ? false : 3000,
  });
}

export function useRestartOnboarding() {
  const clientId = useOnboardingClientId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => OnboardingAPI.restart(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export function useSkipOnboarding() {
  const clientId = useOnboardingClientId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => OnboardingAPI.skip(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}
